<template>
  <div class="productos-wrapper">
    <ModalLoading :isLoading="isLoading" />

    <div class="opciones">
      <!-- Botones principales agrupados -->
      <div class="primary-buttons">
        <button
          v-if="puedeCrearProducto"
          id="btnAdd"
          class="btn btn-primary"
          @click="openModal"
        >
          Agregar Producto
        </button>

        <!-- Importación masiva — solo CEO -->
        <template v-if="esCeo">
          <button class="btn btn-plantilla" @click="descargarPlantilla" title="Descargar plantilla Excel para importación masiva">
            <i class="bi bi-file-earmark-arrow-down"></i> Plantilla Excel
          </button>
          <button class="btn btn-importar" @click="abrirModalImportar" title="Importar productos desde Excel">
            <i class="bi bi-upload"></i> Importar Excel
          </button>
        </template>

        <!-- Botón de exportación PDF -->
        <ExportButton
          :columns="columns"
          :rows="rows"
          fileName="Productos.pdf"
          class="export-button"
        />

        <RouterLink to="promociones-producto" class="promocion-link">
          <button class="button-promocion">Promociones</button>
        </RouterLink>
      </div>

      <!-- Búsqueda y filtros agrupados -->
      <div class="search-filters">
        <div class="search-bar">
          <input
            class="busqueda"
            type="text"
            v-model="searchQuery"
            placeholder="Buscar producto..."
          />
        </div>

        <div class="registros">
          <select
            class="custom-select select-sucursal"
            v-model="searchSucursal"
            v-show="sucursales.length > 1"
            @change="mostrarRegistros(searchSucursal)"
          >
            <option value="default">Todas</option>
            <option
              v-for="(sucursal, index) in sucursales"
              :key="index"
              :value="sucursal.id_sucursal"
            >
              {{ sucursal.nombre_administrativo }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tabla exportable -->
    <div class="table-container" v-pdf-export ref="table">
      <!-- Indicador de carga -->
      <div v-if="isLoading" class="loading-indicator">
        Cargando productos...
      </div>

      <!-- Mensaje si no hay datos -->
      <div v-else-if="paginatedProductos.length === 0" class="no-data">
        No se encontraron productos para mostrar.
      </div>
      <table class="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Código</th>
            <th>Nombre</th>
            <th>Stock</th>
            <th>Precio Unitario</th>
            <th v-if="tieneProduccion">Tipo</th>
            <th>Promoción Activa</th>
            <th>Etiqueta</th>
            <th v-if="puedeAccionesProducto">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(producto, index) in paginatedProductos" :key="index">
            <td data-label="#">
              {{ (currentPage - 1) * pageSize + index + 1 }}
            </td>
            <td data-label="Código">{{ producto.codigo_producto }}</td>
            <td data-label="Nombre">{{ producto.nombre }}</td>
            <td data-label="Stock">
              {{ producto.stock_actual }}
              <button class="edit-stock" @click="openModalStock(producto)">
                <i class="bi bi-pencil-square"></i>
              </button>
            </td>
            <td data-label="Precio Unitario">{{ producto.precio_unitario }}</td>
            <td v-if="tieneProduccion" data-label="Tipo">
              <span class="tipo-badge" :class="'tipo-' + (producto.tipo || 'general')">
                {{ producto.tipo === 'materia_prima' ? 'Materia Prima'
                  : producto.tipo === 'producto_terminado' ? 'Terminado'
                  : 'General' }}
              </span>
            </td>
            <td data-label="Promoción Activa">{{ producto.promocion }}</td>
            <td data-label="Etiqueta">
              <button class="btn-barcode" @click="imprimirEtiqueta(producto)" title="Imprimir etiqueta de código de barras">
                <i class="bi bi-upc-scan"></i>
              </button>
            </td>
            <td v-if="puedeAccionesProducto" data-label="Acciones">
              <button
                v-if="puedeEditarProducto"
                class="accion-btn btn-editar"
                @click="editProducto(producto, index)"
                title="Editar producto"
              >
                <i class="bi bi-pencil-fill"></i>
              </button>
              <button
                v-if="puedeEliminarProducto"
                class="accion-btn btn-eliminar"
                @click="deleteProducto(producto)"
                title="Eliminar producto"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación -->
      <div class="pagination-wrapper">
        <div class="pagination-info">
          Mostrando
          {{
            paginatedProductos.length === 0
              ? 0
              : (currentPage - 1) * pageSize + 1
          }}
          a {{ Math.min(currentPage * pageSize, filteredProductos.length) }} de
          {{ filteredProductos.length }} registros
        </div>
        <div class="pagination-container">
          <button
            class="pagination-button"
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            Anterior
          </button>
          <button
            class="pagination-button"
            :disabled="currentPage === totalPages || totalPages === 0"
            @click="nextPage"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>

    <div class="modal" v-if="showConfirmModal">
      <div class="modal-confirm">
        <div class="modal-header">
          <h2>Confirmación</h2>
        </div>

        <div class="modal-body-confirm">
          <p v-if="productoToDelete.stock_actual > 0">
            Este producto actualmente tiene
            {{ productoToDelete.stock_actual }} unidades en stock. ¿Estás seguro
            de que quieres eliminarlo?
          </p>
          <p v-else>¿Estás seguro de que quieres eliminar este producto?</p>
        </div>

        <div class="modal-footer">
          <div class="action-buttons">
            <btnGuardarModal
              texto="Sí, eliminar"
              style="background-color: red"
              @click="deleteProducto()"
            />
            <btnCerrarModal texto="No, regresar" @click="cancelDelete" />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para agregar o editar productos -->
    <div v-if="isModalOpen" class="modal">
      <div class="modal-content modal-producto">
        <div class="modal-header">
          <h2>{{ isEditing ? "Editar Producto" : "Agregar Producto" }}</h2>
        </div>

        <div class="modal-body">
          <div class="form-row">
            <div class="form-group full-width">
              <label>Código único del producto:</label>
              <input
                ref="codigoInput"
                v-model="productoForm.codigo_producto"
                type="text"
                placeholder="Ingrese el código del producto"
                required
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label>Nombre Producto:</label>
              <input
                v-model="productoForm.nombre"
                type="text"
                placeholder="Ingrese el nombre del producto"
                required
              />
            </div>
          </div>

          <div class="form-columns">
            <div class="form-column">
              <div class="form-group">
                <label>Descripción:</label>
                <textarea
                  v-model="productoForm.descripcion"
                  placeholder="Ingrese la descripción del producto"
                  required
                />
              </div>

              <div class="form-group" v-if="tieneProduccion">
                <label>Tipo de producto:</label>
                <select class="form-select" v-model="productoForm.tipo">
                  <option value="general">General</option>
                  <option value="materia_prima">Materia Prima</option>
                  <option value="producto_terminado">Producto Terminado</option>
                </select>
              </div>

              <div class="form-group" v-if="productoForm.tipo !== 'materia_prima'">
                <label>Impuesto:</label>
                <select class="form-select" v-model="productoForm.impuesto">
                  <option value="" disabled>Seleccione un impuesto</option>
                  <option
                    v-for="(impuesto, index) in cargarImpuestos()"
                    :key="index"
                    :value="impuesto.id"
                  >
                    {{ impuesto.nombre }}
                  </option>
                </select>
              </div>

              <div class="form-group" v-if="productoForm.tipo !== 'producto_terminado'">
                <label>Proveedor:</label>
                <select class="form-select" v-model="productoForm.proveedor">
                  <option value="default" disabled>
                    Seleccione un proveedor
                  </option>
                  <option
                    v-for="(proveedor, index) in proveedoresMostrar"
                    :key="index"
                    :value="proveedor.id"
                  >
                    {{ proveedor.nombre }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-column">
              <div class="form-group" v-if="productoForm.tipo !== 'materia_prima'">
                <label>Precio por Unidad:</label>
                <div class="input-icon">
                  <span class="currency-symbol">L.</span>
                  <input
                    v-model="productoForm.precio_unitario"
                    type="text"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    @blur="formatearPrecio($event, 'precio_unitario')"
                    @keypress="soloNumeros($event)"
                    required
                  />
                </div>
              </div>

              <div class="form-group" v-if="productoForm.tipo !== 'materia_prima'">
                <label>Precio por mayoreo: <small style="font-weight:normal;color:#888;">(opcional)</small></label>
                <div class="input-icon">
                  <span class="currency-symbol">L.</span>
                  <input
                    v-model="productoForm.precio_mayorista"
                    type="text"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    @blur="formatearPrecio($event, 'precio_mayorista')"
                    @keypress="soloNumeros($event)"
                  />
                </div>
              </div>

              <div class="form-group" v-if="productoForm.tipo !== 'materia_prima'">
                <label>Cantidad activar mayoreo: <small style="font-weight:normal;color:#888;">(opcional)</small></label>
                <input
                  @keypress="soloNumeros($event)"
                  v-model="productoForm.cantidad_activar_mayorista"
                  type="text"
                  min="0"
                  placeholder="0"
                />
              </div>

              <div class="form-group">
                <label>{{ productoForm.tipo === 'materia_prima' ? 'Unidad de producción (recetas):' : 'Unidad de medida:' }}</label>
                <select
                  class="form-select"
                  v-model="productoForm.unidad_medida"
                >
                  <option value="default" disabled>
                    Seleccione una unidad de medida
                  </option>
                  <option
                    v-for="(unidad, index) in unidadesMostrar"
                    :key="index"
                    :value="unidad.id_medida"
                  >
                    {{ unidad.medida }}
                  </option>
                </select>
              </div>

              <!-- Unidad de compra y factor de conversión — solo para materias primas -->
              <div v-if="productoForm.tipo === 'materia_prima'" class="form-group">
                <label>Unidad de compra: <small style="font-weight:normal;color:#888;">(opcional, si difiere de la de producción)</small></label>
                <input
                  v-model="productoForm.unidad_compra"
                  type="text"
                  class="form-select"
                  placeholder="Ej: Libra, KG, Saco..."
                  maxlength="50"
                />
              </div>

              <div v-if="productoForm.tipo === 'materia_prima' && productoForm.unidad_compra" class="form-group">
                <label>
                  Factor de conversión:
                  <small style="font-weight:normal;color:#888;">
                    1 {{ productoForm.unidad_compra }} = ? unidades de producción
                  </small>
                </label>
                <input
                  v-model.number="productoForm.factor_conversion"
                  type="number"
                  class="form-select"
                  placeholder="Ej: 3 (1 Libra = 3 plátanos)"
                  min="0.001"
                  step="0.001"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-categoria" @click="openModalCategoria">
            <i class="bi bi-tags"></i>
            Asignar categorías
          </button>
          <div id="btnCanc" class="action-buttons">
            <btnGuardarModal
              :texto="isEditing ? 'Guardar Cambios' : 'Agregar Producto'"
              @click="guardarProducto"
            />
            <btnCerrarModal texto="Cerrar" @click="closeModal" />
          </div>
        </div>
      </div>
    </div>

    <!-- modal para mostrar categorias -->
    <div v-if="isModalCategoriaOpen" class="modal">
      <div class="modal dark">
        <div class="modal-content modal-categorias">
          <div class="categoria-header">
            <h3>Selecciona categorías del producto</h3>
            <div class="search-container">
              <input
                type="text"
                v-model="searchCategoria"
                placeholder="Buscar categoría..."
                class="search-input"
              />
            </div>
          </div>

          <div class="categorias-container">
            <div class="categorias-grid">
              <label
                v-for="(categoria, index) in filteredCategorias"
                :key="index"
                class="categoria-checkbox"
              >
                <input
                  type="checkbox"
                  v-model="categoriasSeleccionadas"
                  :value="categoria.id_categoria"
                />
                <span class="checkbox-text">{{
                  categoria.nombre_categoria
                }}</span>
              </label>
            </div>
          </div>

          <div class="categoria-footer">
            <btnGuardarModal
              :texto="'Agregar Categoría'"
              @click="guardarCategorias"
            >
            </btnGuardarModal>
            <btnCerrarModal
              :texto="'Cerrar'"
              @click="closeModalCategoria"
            ></btnCerrarModal>
          </div>
        </div>
      </div>
    </div>

    <!-- modal para editar stocks min y max por sucursal -->
    <div v-if="isModalStockOpen" class="modal">
      <div class="modal-stock">
        <div class="modal-header">
          <h3>Edite stock min y max por sucursal</h3>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <select
              class="custom-select select-sucursal-stock"
              @change="mostrarStockbySucursal(searchSucursalStock)"
              v-model="searchSucursalStock"
              v-show="sucursales.length > 1"
            >
              <option
                v-for="(sucursal, index) in this.sucursales"
                :key="index"
                :value="sucursal.id_sucursal"
              >
                {{ sucursal.nombre_administrativo }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label for="stock-min">Codigo Producto:</label>
            <label class="label-stock-info">{{ codigoProductoStock }}</label>
          </div>
          <div class="form-group">
            <label for="stock-min">Nombre Producto:</label>
            <label class="label-stock-info"> {{ nombreProductoStock }}</label>
          </div>
          <div class="form-group">
            <label for="stock-min">Stock Min:</label>
            <input
              type="text"
              class="input"
              @keypress="soloNumeros($event)"
              placeholder="Ingrese stock minimo del producto "
              v-model="inputStockMin"
            />
          </div>

          <div class="form-group">
            <label for="stock-max">Stock Max:</label>
            <input
              type="text"
              class="input"
              @keypress="soloNumeros($event)"
              placeholder="Ingrese stock maximo del producto"
              v-model="inputStockMax"
            />
          </div>
        </div>

        <div class="modal-footer">
          <btnGuardarModal
            :texto="'Guardar cambios'"
            @click="guardarStock(searchSucursalStock)"
          >
          </btnGuardarModal>
          <btnCerrarModal
            :texto="'Cerrar'"
            @click="closeModalStock"
          ></btnCerrarModal>
        </div>
      </div>
    </div>
    <!-- ═══════════════════════════════════════════════════════════
         MODAL IMPORTAR EXCEL — solo CEO
    ════════════════════════════════════════════════════════════ -->
    <div v-if="importarModal.open" class="modal">
      <div class="modal-content modal-importar">
        <div class="modal-header">
          <h2><i class="bi bi-file-earmark-spreadsheet"></i> Importar Productos desde Excel</h2>
        </div>

        <!-- PASO 1: Subir archivo -->
        <div v-if="importarModal.paso === 1" class="modal-body">
          <div class="import-instrucciones">
            <p>Sube el archivo Excel con los productos a importar. Usa la plantilla oficial para evitar errores.</p>
            <ul>
              <li>Los campos marcados con <strong>*</strong> son obligatorios.</li>
              <li>Se omitirán productos cuyo código o nombre ya existan en la empresa.</li>
              <li>Se omitirán filas con campos obligatorios vacíos.</li>
              <li>Máximo <strong>500 productos</strong> por importación.</li>
            </ul>
          </div>

          <div
            class="import-dropzone"
            :class="{ dragging: importarModal.dragging, 'has-file': importarModal.archivo }"
            @dragover.prevent="importarModal.dragging = true"
            @dragleave.prevent="importarModal.dragging = false"
            @drop.prevent="onDropImportar"
            @click="$refs.excelInput.click()"
          >
            <input ref="excelInput" type="file" accept=".xlsx,.xls" style="display:none" @change="onArchivoExcel" />
            <template v-if="!importarModal.archivo">
              <i class="bi bi-file-earmark-spreadsheet import-drop-icon"></i>
              <p class="import-drop-text">Arrastra el archivo Excel aquí<br>o <u>haz clic para seleccionarlo</u></p>
              <p class="import-drop-hint">.xlsx o .xls · máx. 500 filas</p>
            </template>
            <template v-else>
              <i class="bi bi-file-earmark-check import-drop-icon selected"></i>
              <p class="import-drop-text">{{ importarModal.archivo.name }}</p>
              <p class="import-drop-hint">{{ importarModal.filasLeidas }} filas leídas — clic para cambiar</p>
            </template>
          </div>

          <p v-if="importarModal.errorLectura" class="import-error-msg">
            <i class="bi bi-exclamation-triangle-fill"></i> {{ importarModal.errorLectura }}
          </p>
        </div>

        <!-- PASO 2: Preview + errores -->
        <div v-if="importarModal.paso === 2" class="modal-body">
          <div class="import-resumen-preview">
            <div class="import-stat ok">
              <i class="bi bi-check-circle-fill"></i>
              <div><span class="stat-num">{{ importarModal.preview.filter(r=>!r._error).length }}</span><span class="stat-lbl">listos para crear</span></div>
            </div>
            <div class="import-stat skip" v-if="importarModal.preview.filter(r=>r._error).length">
              <i class="bi bi-exclamation-triangle-fill"></i>
              <div><span class="stat-num">{{ importarModal.preview.filter(r=>r._error).length }}</span><span class="stat-lbl">filas con error</span></div>
            </div>
          </div>

          <div class="import-table-wrap">
            <table class="import-table">
              <thead>
                <tr>
                  <th>Fila</th><th>Código</th><th>Nombre</th>
                  <th>Precio Unit.</th><th>Estado</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in importarModal.preview"
                  :key="i"
                  :class="row._error ? 'fila-error' : 'fila-ok'"
                >
                  <td>{{ row._fila }}</td>
                  <td>{{ row.codigo_producto }}</td>
                  <td>{{ row.nombre }}</td>
                  <td>{{ row.precio_unitario != null ? 'L. ' + Number(row.precio_unitario).toFixed(2) : '—' }}</td>
                  <td>
                    <span v-if="!row._error" class="badge-ok"><i class="bi bi-check2"></i> Válido</span>
                    <span v-else class="badge-err" :title="row._error"><i class="bi bi-x-lg"></i> {{ row._error }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- PASO 3: Resultado final -->
        <div v-if="importarModal.paso === 3" class="modal-body">
          <div class="import-resultado">
            <div class="resultado-icon ok"><i class="bi bi-check-circle-fill"></i></div>
            <h3>Importación completada</h3>
            <p><strong>{{ importarModal.resultado.creados }}</strong> productos creados correctamente.</p>
            <p v-if="importarModal.resultado.omitidos > 0" class="resultado-warn">
              <strong>{{ importarModal.resultado.omitidos }}</strong> filas omitidas.
            </p>

            <div v-if="importarModal.resultado.detalle_omitidos?.length" class="import-table-wrap" style="margin-top:12px">
              <p style="font-weight:600;margin-bottom:6px">Detalle de omitidos:</p>
              <table class="import-table">
                <thead><tr><th>Fila</th><th>Código</th><th>Nombre</th><th>Motivo</th></tr></thead>
                <tbody>
                  <tr v-for="(o,i) in importarModal.resultado.detalle_omitidos" :key="i" class="fila-error">
                    <td>{{ o.fila }}</td><td>{{ o.codigo || '—' }}</td><td>{{ o.nombre || '—' }}</td>
                    <td>{{ o.motivo }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Pie de modal -->
        <div class="modal-footer">
          <template v-if="importarModal.paso === 1">
            <button class="btn btn-success" :disabled="!importarModal.archivo" @click="previsualizarImportar">
              <i class="bi bi-eye"></i> Previsualizar
            </button>
            <btnCerrarModal texto="Cancelar" @click="cerrarModalImportar" />
          </template>
          <template v-if="importarModal.paso === 2">
            <button class="btn btn-importar" :disabled="importarModal.importando || importarModal.preview.filter(r=>!r._error).length === 0" @click="confirmarImportar">
              <span v-if="!importarModal.importando"><i class="bi bi-cloud-upload"></i> Importar {{ importarModal.preview.filter(r=>!r._error).length }} producto{{ importarModal.preview.filter(r=>!r._error).length !== 1 ? 's' : '' }}</span>
              <span v-else><i class="bi bi-hourglass-split"></i> Importando...</span>
            </button>
            <button class="btn btn-plantilla" @click="importarModal.paso = 1">
              <i class="bi bi-arrow-left"></i> Volver
            </button>
            <btnCerrarModal texto="Cancelar" @click="cerrarModalImportar" />
          </template>
          <template v-if="importarModal.paso === 3">
            <button class="btn btn-plantilla" @click="cerrarModalImportar">
              <i class="bi bi-check2"></i> Cerrar
            </button>
          </template>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
//compontentes
import ExportButton from "../components/ExportButton.vue";
import btnGuardarModal from "../components/botones/modales/btnGuardar.vue";
import btnCerrarModal from "../components/botones/modales/btnCerrar.vue";
import { validacionesProductos } from "../../services/validarCampos.js";
import { notis } from "../../services/notificaciones.js";
import ModalLoading from "@/components/ModalLoading.vue";

// importando solicitudes
import solicitudes from "../../services/solicitudes.js";
const { getSocket } = require('@/services/socket');

//apis
import { getUnidadMedidaEmpresas } from "../../services/unidadMedidaSolicitud.js";
import { getProveedoresEmpresa } from "../../services/proveedoresSolicitud.js";

//solicitudes a api
import * as XLSX from "xlsx";
import {
  getInfoExtra,
  getProductoSucursal,
  postProducto,
  patchProducto,
  desactivarProducto,
  getProductosEmpresa,
  getStockMinMax,
  PostStockMinMax,
  importarProductos,
} from "../../services/productosSolicitudes.js";
import { getCategoriaProductosEmpresa } from "../../services/categoriaSolicitudes.js";
const { esCeo } = require("../../services/usuariosSolicitudes");
import { hasAccionPermiso } from '../../services/permisos.js';
const { impuestos } = require("../resources/impuestos.js");
import { setPageTitle } from "@/components/pageMetadata";

export default {
  components: {
    ExportButton,
    btnGuardarModal,
    btnCerrarModal,
    ModalLoading,
  },
  data() {
    return {
      titulo: "Productos",
      showConfirmModal: false,
      isLoading: false,
      productoToDelete: null,
      searchQuery: "",
      searchCategoria: "",
      searchSucursal: "default",
      searchSucursalStock: "",
      isModalOpen: false,
      isModalCategoriaOpen: false,
      isModalStockOpen: false,
      isEditing: false,
      editIndex: null,
      inputStockMax: 0,
      inputStockMin: 0,
      idProductoStock: "",
      currentPage: 1,
      pageSize: 10,
      id_usuario: "",
      productoOriginal: null,
      unidadesMostrar: [],
      proveedoresMostrar: [],
      esCeo: false,
      tieneProduccion: false,
      productoForm: {
        codigo_producto: "",
        nombre: "",
        descripcion: "",
        unidad_medida: "default",
        impuesto: impuestos[0]?.id || null,
        proveedor: "default",
        precio_unitario: "",
        precio_mayorista: "",
        cantidad_activar_mayorista: "",
        categorias: [],
        tipo: "general",
        unidad_compra: "",
        factor_conversion: 1,
      },
      productos: [],
      sucursales: [],
      categoriasSeleccionadas: [],
      categorias: [],
      // ── Importación masiva ──
      importarModal: {
        open:         false,
        paso:         1,         // 1=subir, 2=preview, 3=resultado
        dragging:     false,
        archivo:      null,
        filasLeidas:  0,
        errorLectura: null,
        preview:      [],        // filas parseadas (con _fila, _error)
        importando:   false,
        resultado:    null,
      },
      columns: [
        { header: "#", dataKey: "index" },
        { header: "Código", dataKey: "codigo_producto" },
        { header: "Descripción", dataKey: "descripcion" },
        { header: "Categoría", dataKey: "categoria" },
        { header: "Stock", dataKey: "stock" },
        { header: "Precio Unitario", dataKey: "preciounitario" },
        { header: "Precio Mayorista", dataKey: "preciomayorista" },
        { header: "Precio Descuento", dataKey: "preciodescuento" },
        { header: "Fecha", dataKey: "fecha" },
      ],
      rows: [],
    };
  },

  computed: {
    hasChanges() {
      if (!this.productoOriginal) return false;
      const changedFields = this.getChangedFields();
      return Object.keys(changedFields).length > 0;
    },

    puedeCrearProducto()    { return hasAccionPermiso('productos_crear'); },
    puedeEditarProducto()   { return hasAccionPermiso('productos_editar'); },
    puedeEliminarProducto() { return hasAccionPermiso('productos_eliminar'); },
    puedeAccionesProducto() { return this.puedeEditarProducto || this.puedeEliminarProducto; },
    filteredProductos() {
      return this.productos.filter(
        (producto) =>
          producto.codigo_producto
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase()) ||
          producto.nombre.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },

    paginatedProductos() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredProductos.slice(startIndex, endIndex);
    },

    totalPages() {
      return Math.ceil(this.filteredProductos.length / this.pageSize);
    },

    filteredCategorias() {
      return this.categorias.filter((categoria) =>
        categoria.nombre_categoria
          .toLowerCase()
          .includes(this.searchCategoria.toLowerCase())
      );
    },
  },

  codigoProductoStock: "",
  nombreProductoStock: "",

  methods: {
    async mostrarRegistros(valor) {
      if (this.sucursales.length === 1) {
        valor = this.sucursales[0].id_sucursal;
        this.searchSucursal = this.sucursales[0].id_sucursal;
      }

      if (valor === "default") {
        this.productos = await getProductosEmpresa(this.id_usuario);
      } else {
        try {
          this.productos = await getProductoSucursal(valor);
        } catch (error) {
          // search error ignored; list stays as-is
        }
      }
    },

    soloNumeros(event) {
      const codigoTecla = event.keyCode || event.which;
      const tecla = String.fromCharCode(codigoTecla);
      const regex = /^[0-9.]$/;
      if (
        !regex.test(tecla) ||
        (tecla === "." && event.target.value.includes("."))
      ) {
        event.preventDefault();
        return false;
      }
    },

    formatearPrecio(event, campo) {
      let valor = event.target.value;
      if (valor === "") return;
      const numeroFormateado = parseFloat(valor).toFixed(2);
      this.productoForm[campo] = numeroFormateado;
    },

    async openModal() {
      this.isLoading = true;
      await this.cargarUnidadMedidaProveedores();
      this.isModalOpen = true;
      this.isLoading = false;
      this.$nextTick(() => {
        this.$refs.codigoInput?.focus();
      });
    },

    closeModal() {
      this.isModalOpen = false;
      this.clearForm();
    },

    async openModalCategoria() {
      this.isLoading = true;
      this.categorias = await getCategoriaProductosEmpresa(this.id_usuario);
      this.isModalCategoriaOpen = true;
      this.isLoading = false;
    },

    guardarCategorias() {
      this.isLoading = true;
      this.productoForm.categorias = this.categoriasSeleccionadas;
      this.closeModalCategoria();
      this.isLoading = false;
    },

    closeModalCategoria() {
      this.isModalCategoriaOpen = false;
    },

    async mostrarStockbySucursal(id_sucursal) {
      this.isLoading = true;
      try {
        const response = await getStockMinMax(
          this.idProductoStock,
          id_sucursal
        );
        if (response.error) {
          console.error("Error al obtener stock:", response.error);
          return;
        }
        this.inputStockMin = response.stock_min || 0;
        this.inputStockMax = response.stock_max || 0;
      } catch (error) {
        console.error("Error al abrir el modal de stock:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async openModalStock(producto) {
      this.codigoProductoStock = producto.codigo_producto;
      this.nombreProductoStock = producto.nombre;
      this.idProductoStock = producto.id_producto;

      if (this.searchSucursal === "default") {
        if (!this.sucursales?.length) {
          this.toast.error('No hay sucursales disponibles');
          return;
        }
        this.searchSucursalStock = this.sucursales[0].id_sucursal;
      } else {
        this.searchSucursalStock = this.searchSucursal;
      }
      await this.mostrarStockbySucursal(this.searchSucursalStock);
      this.isModalStockOpen = true;
    },

    async guardarStock(sucursal) {
      const stock_min = Number(this.inputStockMin);
      const stock_max = Number(this.inputStockMax);

      const datosStock = {
        stock_min: stock_min,
        stock_max: stock_max,
      };

      if (stock_max <= stock_min) {
        notis(
          "error",
          "Datos invalidos: Stock minimo no puede ser mayor o igual a stock maximo"
        );
        return;
      }

      try {
        const response = await PostStockMinMax(
          this.idProductoStock,
          sucursal,
          datosStock
        );
        if (response instanceof Error) {
          throw response;
        }
        notis("success", "Datos de stock guardados correctamente");
      } catch (error) {
        notis("error", error);
      }

      this.closeModalStock();
    },

    closeModalStock() {
      this.isModalStockOpen = false;
    },

    clearForm() {
      this.productoForm = {
        codigo_producto: "",
        nombre: "",
        descripcion: "",
        unidad_medida: "default",
        impuesto: impuestos[0]?.id || null,
        proveedor: "default",
        precio_unitario: "",
        precio_mayorista: "",
        cantidad_activar_mayorista: "",
        categorias: [],
        tipo: "general",
        unidad_compra: "",
        factor_conversion: 1,
      };
      this.productoOriginal = null;
      this.isEditing = false;
      this.editIndex = null;
      this.categoriasSeleccionadas = [];
    },
    cargarImpuestos() {
      return impuestos;
    },

    async cargarUnidadMedidaProveedores() {
      this.unidadesMostrar = await getUnidadMedidaEmpresas(this.id_usuario);
      this.proveedoresMostrar = await getProveedoresEmpresa(this.id_usuario);
    },

    async guardarProducto() {
      if (!validacionesProductos.validarCampos(this.productoForm)) {
        return;
      }

      if (!this.tieneProduccion) {
        this.productoForm.tipo = 'general';
      }

      if (this.productoForm.tipo === 'materia_prima') {
        this.productoForm.precio_unitario = 0;
        this.productoForm.precio_mayorista = 0;
        this.productoForm.cantidad_activar_mayorista = 0;
        this.productoForm.impuesto = 3; // IMPUESTO_EXTENTO
      }

      if (this.productoForm.tipo === 'producto_terminado') {
        this.productoForm.proveedor = null;
      }

      if (this.productoForm.precio_mayorista === '' || this.productoForm.precio_mayorista === undefined)
        this.productoForm.precio_mayorista = null;
      if (this.productoForm.cantidad_activar_mayorista === '' || this.productoForm.cantidad_activar_mayorista === undefined)
        this.productoForm.cantidad_activar_mayorista = null;

      this.isLoading = true;

      try {
        if (this.isEditing) {
          const changedFields = this.getChangedFields();

          // Si no hay cambios, cerrar modal
          if (Object.keys(changedFields).length === 0) {
            this.closeModal();
            return;
          }

          // Agregar id_usuario si es necesario
          changedFields.id_usuario = this.id_usuario;

          // Si el código no cambió, no lo enviamos
          if (
            changedFields.codigo_producto ===
            this.productos[this.editIndex].codigo_producto
          ) {
            delete changedFields.codigo_producto;
          }

          const nuevoRegistro = await patchProducto(
            changedFields,
            this.productos[this.editIndex].id_producto
          );

          if (nuevoRegistro === true) {
            notis("success", "Actualizando datos del producto...");
            // Actualizar solo los campos modificados en el array de productos
            Object.assign(this.productos[this.editIndex], changedFields);
          }
        } else {
          // Lógica para nuevo producto
          this.productoForm.id_usuario = this.id_usuario;
          const nuevoRegistro = await postProducto(this.productoForm);
          this.productos.push(nuevoRegistro[0]);
          notis("success", "Producto agregado correctamente");
        }
      } catch (error) {
        notis("error", error.message);
      } finally {
        this.isLoading = false;
        this.closeModal();
      }
    },

    async editProducto(producto, index) {
      // Inicializar el form solo con datos básicos
      this.productoForm = {
        codigo_producto: producto.codigo_producto || "",
        nombre: producto.nombre || "",
        descripcion: producto.descripcion || "",
        precio_unitario: producto.precio_unitario || "",
        precio_mayorista: "",
        proveedor: "default",
        unidad_medida: "default",
        impuesto: impuestos[0]?.id || null,
        cantidad_activar_mayorista: "",
        categorias: [],
        tipo: producto.tipo || "general",
        unidad_compra: "",
        factor_conversion: 1,
      };

      try {
        // Obtener info extra y solo actualizar los campos necesarios
        const infoExtra = await getInfoExtra(producto.id_producto);

        // Solo actualizar campos si son diferentes
        if (infoExtra.impuesto !== undefined) {
          this.productoForm.impuesto = infoExtra.impuesto;
        }
        if (infoExtra.id_unidad_medida) {
          this.productoForm.unidad_medida = infoExtra.id_unidad_medida;
        }
        if (infoExtra.id_proveedor) {
          this.productoForm.proveedor = infoExtra.id_proveedor;
        }
        if (infoExtra.precio_mayorista !== undefined) {
          this.productoForm.precio_mayorista = infoExtra.precio_mayorista;
        }
        if (infoExtra.cantidad_activar_mayorista !== undefined) {
          this.productoForm.cantidad_activar_mayorista =
            infoExtra.cantidad_activar_mayorista;
        }
        if (infoExtra.categorias) {
          this.categoriasSeleccionadas = infoExtra.categorias;
        }
        if (infoExtra.tipo) {
          this.productoForm.tipo = infoExtra.tipo;
        }
        if (infoExtra.unidad_compra !== undefined) {
          this.productoForm.unidad_compra = infoExtra.unidad_compra || "";
        }
        if (infoExtra.factor_conversion !== undefined) {
          this.productoForm.factor_conversion = Number(infoExtra.factor_conversion) || 1;
        }

        this.isEditing = true;
        this.editIndex = index;
        this.productoOriginal = { ...this.productoForm }; // Guardar estado original
        this.openModal();
      } catch (error) {
        notis("error", "Error al editar el producto");
        console.error(error);
      }
    },

    getChangedFields() {
      const changedFields = {};
      Object.keys(this.productoForm).forEach((key) => {
        // Comparar con los valores originales
        if (this.productoForm[key] !== this.productoOriginal[key]) {
          changedFields[key] = this.productoForm[key];
        }
      });
      return changedFields;
    },

    // ═══════ PLANTILLA + IMPORTACIÓN EXCEL ═══════════════════════════════
    descargarPlantilla() {
      const wb = XLSX.utils.book_new();

      // ── Hoja 1: Plantilla de datos ────────────────────────────────────────
      const encabezados = [
        ['codigo_producto *', 'nombre *', 'descripcion', 'precio_unitario *', 'precio_mayorista', 'cantidad_activar_mayorista', 'impuesto_id', 'unidad_medida_id'],
        ['Código único', 'Nombre del producto', 'Descripción (opcional)', 'Precio unitario en L.', 'Precio mayorista en L. (opcional)', 'Cantidad para activar precio mayorista (opcional)', 'ID de impuesto (ver Hoja2)', 'ID de unidad de medida (ver Hoja2)'],
        // Fila de ejemplo
        ['PROD-001', 'Ejemplo Producto', 'Descripción de ejemplo', 99.90, '', '', 3, ''],
      ];
      const wsProductos = XLSX.utils.aoa_to_sheet(encabezados);
      wsProductos['!cols'] = [
        { wch: 18 }, { wch: 28 }, { wch: 30 }, { wch: 20 },
        { wch: 20 }, { wch: 32 }, { wch: 24 }, { wch: 24 },
      ];

      // ── Hoja 2: Catálogos de referencia (datos reales de la empresa) ──────
      // Impuestos (siempre los mismos)
      const impuestos = [
        [1, 'ISV 15%'],
        [2, 'ISV 18%'],
        [3, 'Exento (sin impuesto)'],
      ];

      // Unidades de medida de la empresa (ya cargadas en this.unidadesMostrar)
      const unidades = (this.unidadesMostrar || []).map(u => [u.id_medida, u.medida]);

      // Calcular cuántas filas necesita cada sección
      const maxRows = Math.max(impuestos.length, unidades.length, 1);

      const ref = [
        ['=== IMPUESTOS ===', '', '=== UNIDADES DE MEDIDA ===', ''],
        ['impuesto_id', 'Descripción', 'unidad_medida_id', 'Nombre'],
      ];
      for (let i = 0; i < maxRows; i++) {
        ref.push([
          impuestos[i]?.[0] ?? '', impuestos[i]?.[1] ?? '',
          unidades[i]?.[0] ?? '', unidades[i]?.[1] ?? '',
        ]);
      }

      const wsRef = XLSX.utils.aoa_to_sheet(ref);
      wsRef['!cols'] = [
        { wch: 14 }, { wch: 24 },
        { wch: 16 }, { wch: 28 },
      ];

      XLSX.utils.book_append_sheet(wb, wsProductos, 'Productos');
      XLSX.utils.book_append_sheet(wb, wsRef, 'Catálogos');

      XLSX.writeFile(wb, 'Plantilla_Importacion_Productos.xlsx');
    },

    abrirModalImportar() {
      this.importarModal = {
        open: true, paso: 1, dragging: false,
        archivo: null, filasLeidas: 0, errorLectura: null,
        preview: [], importando: false, resultado: null,
      };
    },

    cerrarModalImportar() {
      this.importarModal.open = false;
    },

    onDropImportar(e) {
      this.importarModal.dragging = false;
      const file = e.dataTransfer?.files?.[0];
      if (file) this.procesarArchivoExcel(file);
    },

    onArchivoExcel(e) {
      const file = e.target.files?.[0];
      if (file) this.procesarArchivoExcel(file);
      e.target.value = '';
    },

    procesarArchivoExcel(file) {
      this.importarModal.errorLectura = null;
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(ext)) {
        this.importarModal.errorLectura = 'Solo se aceptan archivos .xlsx o .xls';
        return;
      }
      this.importarModal.archivo = file;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const wb = XLSX.read(ev.target.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          // Leer desde fila 3 (índice 2) para saltar encabezados de plantilla
          const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
          // Detectar si es la plantilla oficial (primera celda contiene "codigo_producto")
          // En ese caso saltar las 2 primeras filas (encabezado + descripción)
          let startRow = 0;
          const primeraCelda = String(raw[0]?.[0] || '').toLowerCase();
          if (primeraCelda.includes('codigo_producto')) startRow = 2;

          const datos = raw.slice(startRow).filter(row => row.some(c => String(c).trim() !== ''));
          this.importarModal.filasLeidas = datos.length;

          // Parsear según posición de columnas de la plantilla:
          // 0:codigo, 1:nombre, 2:descripcion, 3:precio_unitario,
          // 4:precio_mayorista, 5:cantidad_mayorista, 6:impuesto_id, 7:unidad_medida_id
          this.importarModal.preview = datos.map((row, i) => ({
            _fila:        startRow + i + 3,
            _error:       null,
            codigo_producto:            String(row[0] || '').trim(),
            nombre:                     String(row[1] || '').trim(),
            descripcion:                String(row[2] || '').trim(),
            precio_unitario:            row[3] !== '' ? Number(row[3]) : null,
            precio_mayorista:           row[4] !== '' ? Number(row[4]) : null,
            cantidad_activar_mayorista: row[5] !== '' ? Number(row[5]) : null,
            impuesto_id:                row[6] !== '' ? Number(row[6]) : null,
            unidad_medida_id:           row[7] !== '' ? Number(row[7]) : null,
          }));
        } catch (err) {
          this.importarModal.errorLectura = 'Error leyendo el archivo: ' + err.message;
          this.importarModal.archivo = null;
        }
      };
      reader.readAsArrayBuffer(file);
    },

    previsualizarImportar() {
      // Validación client-side antes de enviar al servidor
      const preview = this.importarModal.preview;
      const codigos  = new Set();
      const nombres  = new Set();

      preview.forEach(row => {
        row._error = null;
        const errores = [];
        if (!row.codigo_producto) errores.push('código obligatorio');
        if (!row.nombre)          errores.push('nombre obligatorio');
        if (!row.precio_unitario || row.precio_unitario <= 0) errores.push('precio_unitario debe ser > 0');
        if (codigos.has(row.codigo_producto.toLowerCase())) errores.push('código duplicado en el archivo');
        if (nombres.has(row.nombre.toLowerCase()))           errores.push('nombre duplicado en el archivo');
        if (errores.length) row._error = errores.join('; ');
        if (row.codigo_producto) codigos.add(row.codigo_producto.toLowerCase());
        if (row.nombre)          nombres.add(row.nombre.toLowerCase());
      });

      this.importarModal.paso = 2;
    },

    async confirmarImportar() {
      const validos = this.importarModal.preview.filter(r => !r._error);
      if (!validos.length) return;

      this.importarModal.importando = true;
      try {
        const resultado = await importarProductos(this.id_usuario, validos);
        this.importarModal.resultado = resultado;
        this.importarModal.paso = 3;
        // Recargar lista
        await this.mostrarRegistros(this.searchSucursal);
        if (resultado.creados > 0)
          notis('success', `${resultado.creados} productos importados correctamente`);
      } catch (err) {
        notis('error', 'Error al importar: ' + (err.message || 'Error desconocido'));
      } finally {
        this.importarModal.importando = false;
      }
    },
    // ═══════════════════════════════════════════════════════════════════

    async deleteProducto(producto) {
      if (!this.showConfirmModal) {
        this.productoToDelete = producto;
        this.showConfirmModal = true;
        return;
      }
      this.isLoading = true;
      try {
        const registroEliminado = await desactivarProducto(
          this.productoToDelete.id_producto
        );

        if (registroEliminado == true) {
          this.productos = this.productos.filter(
            (item) => item.id_producto !== this.productoToDelete.id_producto
          );
          this.isLoading = false;
          notis("success", "Producto eliminado correctamente");
        } else {
          this.isLoading = false;
          notis("error", "Error al eliminar producto");
        }
      } catch (error) {
        console.error(error);
        notis("error", "Error al eliminar el producto");
      } finally {
        this.showConfirmModal = false;
        this.productoToDelete = null;
        this.isLoading = false;
      }
    },

    cancelDelete() {
      this.showConfirmModal = false;
      this.productoToDelete = null;
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    generateRows() {
      this.rows = this.paginatedProductos.map((producto, index) => ({
        index: index + 1,
        codigo_producto: producto.codigo_producto,
        descripcion: producto.descripcion,
        categoria: producto.categoria,
        stock: producto.stock,
        preciounitario: producto.preciounitario,
        preciomayorista: producto.preciomayorista,
        preciodescuento: producto.preciodescuento,
        fecha: producto.fecha,
      }));
    },

    changeFavicon(iconPath) {
      const link =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "icon";
      link.href = iconPath;
      document.getElementsByTagName("head")[0].appendChild(link);
    },

    imprimirEtiqueta(producto) {
      const codigo = producto.codigo_producto || '';
      const nombre = producto.nombre || '';
      const precio = Number(producto.precio_unitario || 0).toFixed(2);

      const win = window.open('', '_blank', 'width=420,height=320');
      const sc = 'script';
      const st = 'style';
      const html = '<!DOCTYPE html>'
        + '<html><head><meta charset="UTF-8">'
        + '<title>Etiqueta - ' + nombre + '</title>'
        + '<' + sc + ' src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></' + sc + '>'
        + '<' + st + '>'
        + '* { margin: 0; padding: 0; box-sizing: border-box; }'
        + 'body { font-family: Arial, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #fff; }'
        + '.etiqueta { border: 1px solid #ccc; padding: 10px 14px; text-align: center; width: 280px; }'
        + '.nombre { font-size: 11px; font-weight: bold; margin-bottom: 6px; word-wrap: break-word; text-transform: uppercase; }'
        + '.barcode-wrap { margin: 4px 0; }'
        + '.barcode-wrap svg { width: 100%; }'
        + '.precio { font-size: 16px; font-weight: bold; margin-top: 6px; }'
        + '@media print { body { margin: 0; } }'
        + '</' + st + '>'
        + '</head><body>'
        + '<div class="etiqueta">'
        + '<div class="nombre">' + nombre + '</div>'
        + '<div class="barcode-wrap"><svg id="barcode"></svg></div>'
        + '<div class="precio">L. ' + precio + '</div>'
        + '</div>'
        + '<' + sc + '>'
        + 'JsBarcode("#barcode","' + codigo + '",{format:"CODE128",width:2,height:48,displayValue:true,fontSize:11,margin:2});'
        + 'setTimeout(function(){window.print();},600);'
        + '</' + sc + '>'
        + '</body></html>';
      win.document.write(html);
      win.document.close();
    },
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    searchSucursal() {
      this.currentPage = 1;
    },
    paginatedProductos() {
      this.generateRows();
    },
  },

  async mounted() {
    this.isLoading = true;
    this.generateRows();
    setPageTitle("Productos");

    try {
      this.id_usuario = await solicitudes.solicitarUsuarioToken();
      this.esCeo = await esCeo(this.id_usuario);
      const ep = localStorage.getItem('empresa_plan');
      this.tieneProduccion = ep ? (JSON.parse(ep).tiene_produccion ?? false) : false;
      if (this.esCeo) {
        this.sucursales = await solicitudes.fetchRegistrosAuth(`/sucursales/empresa/${this.id_usuario}`);
        if (!Array.isArray(this.sucursales)) this.sucursales = [];
        // Precargar catálogos para la plantilla Excel
        this.categorias      = await getCategoriaProductosEmpresa(this.id_usuario);
        this.unidadesMostrar = await getUnidadMedidaEmpresas(this.id_usuario);
      } else {
        const suc = await solicitudes.fetchRegistrosAuth(`/sucursales/sucursal-datos/${this.id_usuario}`);
        this.sucursales = suc?.id_sucursal ? [suc] : [];
      }
      this.mostrarRegistros(this.searchSucursal);
    } catch (error) {
      // load error ignored; loading spinner stops via finally
    } finally {
      this.isLoading = false;
    }
    const _s = getSocket();
    if (_s) {
      this._wsRefresh = () => { if (this.id_usuario) this.mostrarRegistros(this.searchSucursal); };
      _s.on('productos:refresh', this._wsRefresh);
    }
  },
  beforeUnmount() {
    const _s = getSocket();
    if (_s && this._wsRefresh) _s.off('productos:refresh', this._wsRefresh);
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

* {
  font-family: "Montserrat", sans-serif;
  box-sizing: border-box;
}

.contenedor-principal {
  display: flex;
  width: 100%;
  justify-content: space-around;
}

.contenedor {
  width: 45%;
}

.opciones {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.busqueda {
  width: 100%;
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 14px;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #c09d62;
  box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
  transition: all 0.3s ease;
}

#btnAdd {
  background-color: #c09d62;
  font-size: 16px;
  width: auto;
  min-width: 170px;
  height: 40px;
  border-radius: 10px;
  color: black;
  font-weight: bold;
  white-space: nowrap;
}

.button-promocion {
  background-color: #3d8d8d;
  color: white;
  padding: 10px 20px;
  border: none;
  cursor: pointer;
  border-radius: 10px;
  margin-left: 15px;
}

.button-promocion:hover {
  background-color: #3d8d8d;
}

.export-button {
  margin: 0;
}

#btnAdd:hover {
  background-color: #a38655;
  transform: scale(1.05);
  transition: all 0.3s ease;
}

#btnEditar {
  font-size: 18px;
  width: 50px;
  height: 40px;
  border-radius: 10px;
}

#btnEditar:hover {
  background-color: #e8af06;
  transform: scale(1.05);
  transition: all 0.3s ease;
}

#btnEliminar {
  font-size: 18px;
  width: 50px;
  height: 40px;
  border-radius: 10px;
  color: black;
}

#btnEliminar:hover {
  background-color: #b72433;
  transform: scale(1.05);
  transition: all 0.3s ease;
}

.productos-wrapper {
  padding: 16px;
  width: 100%;
  overflow-x: hidden;
}

.table-container {
  width: 100%;
  border-radius: 0;
  border: 1px solid #e2e8f0;
  margin-top: 16px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.table {
  width: 100%;
  min-width: 800px;
  border-collapse: separate;
  border-spacing: 0;
}

.table thead {
  position: sticky;
  top: 0;
  z-index: 0;
  background-color: white;
}

.table th,
.table td {
  padding: 8px;
}

.table thead th {
  text-align: center;
  border-bottom: 1px solid #ddd;
}

.table tbody td {
  text-align: center;
  border-top: 1px solid #ddd;
}

.table thead th:first-child {
  border-top-left-radius: 10px;
}

.table thead th:last-child {
  border-top-right-radius: 10px;
}

.table tbody tr:last-child td:first-child {
  border-bottom-left-radius: 10px;
}

.table tbody tr:last-child td:last-child {
  border-bottom-right-radius: 10px;
}

/* Botones de acción en tabla */
.accion-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #fff;
  font-size: 0.8rem;
  margin: 0 2px;
  transition: opacity 0.15s, transform 0.15s;
}
.accion-btn:hover { opacity: .85; transform: scale(1.08); }

.btn-editar   { background: #f59e0b; color: #111; }
.btn-eliminar { background: #ef4444; }

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modalShowConfirm-Si {
  background-color: #dc3545;
  color: white;
}

.modalShowConfirm-no {
  background-color: #6c757d;
  color: white;
}

.modal-confirm {
  background: white;
  border-radius: 12px;
  width: 25%;
  max-width: 1000px;
  max-height: 90vh;
  padding: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  margin: 20px;
  overflow: hidden;
}

.modal-content-categoria {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 30%;
  height: 72%;
  min-height: 500px;
  min-width: 300px;
}

.h2-modal-content {
  margin-top: 0px;
}

.edit-stock {
  padding: 5px;
  border: none;
  border-radius: 5px;
  background-color: #163ef3;
  color: rgb(238, 238, 238);
  font-size: 15px;
  cursor: pointer;
}

.custom-select {
  border: 1px solid #ccc;
  border-radius: 5px;
  height: 35px;
  font-size: 16px;
  padding: 5px;
  background-color: #fff;
  cursor: pointer;
  width: 80px;
}

.select-sucursal {
  min-width: 200px;
}

.custom-select:focus {
  outline: none;
  border-color: #a38655;
}

.custom-select option {
  font-size: 16px;
}

.select-sucursal-stock {
  width: 100%;
}

.btn-export {
  background-color: #4caf50;
  color: white;
  font-size: 16px;
  border-radius: 5px;
  padding: 10px 20px;
  margin-right: 10px;
}

.btn-export:hover {
  background-color: #45a049;
}

/* Pagination styles */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  margin-top: auto;
  background-color: #fff;
}

.pagination-info {
  color: #6c757d;
}

.pagination-container {
  display: flex;
  gap: 5px;
}

.pagination-button {
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background-color: white;
  cursor: pointer;
  border-radius: 4px;
  min-width: 40px;
  transition: all 0.3s ease;
}

.pagination-button:hover:not(:disabled) {
  background-color: #e9ecef;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Estilo para scroll personalizado */
.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c09d62;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a38655;
}

/* Modal de Categorías */
.modal-categorias {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  min-height: 400px;
  max-height: 80vh;
  padding: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
}

.categoria-header {
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  border-radius: 12px 12px 0 0;
}

.dark .categoria-header,
.dark .categoria-footer {
  background-color: #1e1e1e;
  border-color: #404040;
}

.dark .categoria-header h3 {
  color: white;
}

.categoria-header h3 {
  margin: 0 0 16px 0;
  color: #333;
  font-size: 1.5rem;
  font-weight: 600;
}

.search-container {
  position: relative;
  margin-bottom: 8px;
}

.search-bar {
  flex-grow: 1; /* Ocupa el espacio restante */
  max-width: 500px; /* Limita el ancho máximo */
}

.search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: white;
}

.search-input:focus {
  outline: none;
  border-color: #c09d62;
  box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
}

.categorias-container {
  padding: 20px;
  overflow-y: auto;
  flex-grow: 1;
}

.categorias-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding: 8px;
}

.categoria-checkbox {
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
}

.categoria-checkbox:hover {
  background-color: #edf2f7;
  transform: translateY(-1px);
}

.categoria-checkbox input[type="checkbox"] {
  margin-right: 12px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #c09d62;
}

.checkbox-text {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.dark .categoria-checkbox {
  background-color: #343a40;
  /* Fondo oscuro */
  border: 1px solid #495057;
  /* Borde oscuro */
}

.dark .categoria-checkbox:hover {
  background-color: #495057;
}

.dark .categoria-checkbox input[type="checkbox"] {
  accent-color: #f8c927;
  /* Color del checkbox en modo oscuro */
}

.dark .checkbox-text {
  color: #e1e1e1;
  /* Texto claro en modo oscuro */
}

.categoria-footer {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background-color: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

.primary-buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

/* Modal de Producto */
.modal-producto {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  /* Reducido de 1000px */
  max-height: 85vh;
  margin: 20px auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  /* Cambiado de auto a hidden */
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-stock {
  background: white;
  border-radius: 12px;
  width: 35%;
  max-width: 1000px;
  max-height: 90vh;
  padding: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  margin: 20px;
  overflow: hidden;
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
}

.modal-header h2 {
  margin: 0;
  color: #333;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-body-confirm {
  padding: 24px;
  overflow-y: auto;
  background-color: white;
}

.dark .modal-body-confirm {
  background-color: #333;
}

.dark .modal-body {
  background-color: #2d2d2d;
}

/* Form Layout */
.form-row {
  margin-bottom: 20px;
}

.form-columns {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  padding: 0;
  margin-bottom: 20px;
}

.full-width {
  grid-column: 1 / -1;
}

/* Form Groups */

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
}

.form-group label {
  margin: 0;
  color: #333;
  font-weight: 500;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: white;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #c09d62;
  box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
  transition: all 0.3s ease;
}

.btn-categoria {
  background-color: #c09d62;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.dark .btn-categoria {
  background-color: #977c4f;
}

.dark .btn-categoria:hover {
  background-color: #6b5939;
}

.btn-categoria:hover {
  background-color: #696969;
}

.btn-categoria i {
  font-size: 1.2em;
}

.input-icon {
  position: relative;
}

.currency-symbol {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.input-icon input {
  padding-left: 30px;
}

/* Select Styles */
.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23666'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px;
  padding-right: 40px;
}

/* Footer */
.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

/* Focus States */
.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #c09d62;
  box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
}

/* Dark Mode */
.dark .modal-producto {
  background-color: #2d2d2d;
}

.dark .modal-header,
.dark .modal-footer {
  background-color: #1e1e1e;
  border-color: #404040;
}

.dark .modal-header h2 {
  color: #fff;
}

.dark .form-group label {
  color: #fff;
}

.dark .form-group input,
.dark .form-group textarea,
.dark .form-group select {
  background-color: #383838;
  border-color: #404040;
  color: #fff;
}

.dark .currency-symbol {
  color: #aaa;
}

/* Media Queries Principales */

/* Tablets y pantallas medianas */
@media screen and (max-width: 1024px) {
  .modal-producto {
    width: 95%;
  }

  .modal-stock {
    width: 50%;
  }

  .form-columns {
    gap: 16px;
  }
}

/* Tablets */
@media screen and (max-width: 768px) {
  .productos-wrapper {
    padding: 10px;
  }

  .opciones {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }

  /* Ajustes de botones y controles */
  #btnAdd,
  .button-promocion,
  .export-button,
  .busqueda,
  .custom-select {
    width: 100%;
    max-width: none;
    margin: 4px 0;
  }

  .table-container {
    margin-top: 12px;
    overflow-x: auto;
    border-radius: 8px;
  }

  .table {
    min-width: 650px;
  }

  .modal-stock {
    width: 90%;
  }

  /* Ajustes de paginación */
  .pagination-wrapper {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .pagination-info {
    text-align: center;
  }

  .pagination-container {
    justify-content: center;
  }

  .opciones {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
  }

  .action-bar {
    display: flex;
    gap: 10px;
    width: 100%;
  }

  /* Contenedor para los botones principales */
  .primary-buttons {
    display: flex;
    gap: 10px;
    width: 100%;
  }

  #btnAdd {
    flex: 1;
    margin: 0;
  }

  .export-button {
    flex: 1;
    margin: 0;
  }

  .button-promocion {
    width: 100%;
    margin: 0;
  }

  /* Contenedor para búsqueda y filtros */
  .search-filters {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .search-bar {
    width: 100%;
  }

  .busqueda {
    width: 100%;
    max-width: none;
  }

  .registros {
    width: 100%;
  }

  .custom-select {
    width: 100%;
    min-width: 100%;
  }
}

/* Móviles */
@media screen and (max-width: 480px) {
  .productos-wrapper {
    padding: 8px;
  }

  /* Convertir tabla en tarjetas */
  .table {
    display: block;
    min-width: unset;
  }

  .table thead {
    display: none;
  }

  .table tbody {
    display: block;
  }

  .table tr {
    display: block;
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
  }

  .table td {
    display: grid;
    grid-template-columns: 40% 60%;
    padding: 8px 4px;
    text-align: left;
    border: none;
    font-size: 14px;
  }

  .table td::before {
    content: attr(data-label);
    font-weight: bold;
    color: #666;
  }

  /* Ajustes para el modo oscuro en móvil */
  .dark .table tr {
    background: #2d2d2d;
    border-color: #404040;
  }

  .dark .table td::before {
    color: #aaa;
  }

  /* Columna de stock con botón editar */
  .table td:nth-child(4) {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .edit-stock {
    width: 32px;
    height: 32px;
    padding: 4px;
    font-size: 14px;
  }

  /* Columna de acciones */
  .table td:last-child {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    margin-top: 8px;
    border-top: 1px solid #eee;
    grid-column: 1 / -1;
  }

  .dark .table td:last-child {
    border-top-color: #404040;
  }

  /* Ajustes de modales */
  .modal-producto,
  .modal-stock,
  .modal-confirm {
    width: 95%;
    margin: 10px;
  }

  .modal-body {
    padding: 16px;
  }

  .form-group input,
  .form-group textarea,
  .form-group select {
    font-size: 14px;
    padding: 8px 12px;
  }

  /* Ajustes de paginación */
  .pagination-button {
    padding: 6px 12px;
    font-size: 13px;
  }

  .pagination-info {
    font-size: 13px;
  }

  .opciones {
    gap: 8px;
  }

  .primary-buttons {
    flex-direction: column;
  }

  #btnAdd,
  .export-button,
  .button-promocion {
    width: 100%;
    margin: 0;
    height: 40px;
    font-size: 14px;
  }
}

/* Móviles pequeños */
@media screen and (max-width: 360px) {
  .table td {
    grid-template-columns: 100%;
  }

  .table td::before {
    margin-bottom: 4px;
  }

  .table td:nth-child(4),
  .table td:last-child {
    justify-content: center;
  }

  #btnEditar,
  #btnEliminar {
    width: 36px;
    height: 36px;
  }

  .modal-header h2 {
    font-size: 1.2rem;
  }

  .form-group label {
    font-size: 13px;
  }
}

/* Landscape mode */
@media screen and (max-height: 480px) and (orientation: landscape) {
  .modal-producto {
    max-height: 100vh;
    margin: 0;
  }

  .modal-body {
    max-height: calc(100vh - 120px);
  }

  .form-group textarea {
    min-height: 60px;
  }
}

/* =======================================================
   Modo Oscuro
======================================================= */
/* Contenedor principal */
.dark .productos-wrapper {
  background-color: #1e1e1e;
  color: #fff;
}

/* Inputs y búsqueda en modo oscuro */
.dark .busqueda {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #fff;
}

.dark .custom-select {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #fff;
}

.dark .custom-select option {
  background-color: #2d2d2d;
  color: #fff;
}

/* Tabla en modo oscuro */
.dark .table-container {
  background-color: #1e1e1e;
  border-color: #374151;
}

.dark .table {
  background-color: #1e1e1e;
}

.dark .table thead {
  background-color: #111827;
}

.dark .table th {
  background-color: #111827;
  color: #e5e7eb;
  border-bottom-color: #374151;
}

.dark .table td {
  color: #e5e7eb;
  border-bottom-color: #374151;
}

.dark .table tbody tr:hover {
  background-color: #1f2937;
}

/* Modal en modo oscuro */
.dark .modal-content {
  background-color: #2d2d2d;
  color: #fff;
}

.dark .modal-content input,
.dark .modal-content textarea {
  background-color: #383838;
  border-color: #404040;
  color: #fff;
}

.dark .form-group label {
  color: #fff;
}

/* Scroll personalizado en modo oscuro */
.dark .table-container::-webkit-scrollbar-track {
  background: #2d2d2d;
}

.dark .table-container::-webkit-scrollbar-thumb {
  background: #c09d62;
}

.dark .table-container::-webkit-scrollbar-thumb:hover {
  background: #a38655;
}

/* Paginación en modo oscuro */
.dark .pagination-wrapper {
  background-color: #2d2d2d;
  border-color: #404040;
}

.dark .pagination-info {
  color: #aaa;
}

.dark .pagination-button {
  background-color: #383838;
  border-color: #404040;
  color: #fff;
}

.dark .pagination-button:hover:not(:disabled) {
  background-color: #444;
}

.dark .pagination-button:disabled {
  background-color: #2d2d2d;
  color: #666;
}

/* Botones en modo oscuro (manteniendo los colores originales) */
.dark .button-promocion {
  background-color: #4cafaf;
  color: black;
}

.dark .button-unidad-medida {
  background-color: #4caf4c;
  color: #000;
}

.dark #btnAdd {
  background-color: #c09d62;
  color: black;
}

.dark #btnAdd:hover {
  background-color: #a38655;
}

.dark #btnEditar {
  background-color: #ffc107;
  color: black;
}

.dark #btnEditar:hover {
  background-color: #e8af06;
}

.dark #btnEliminar {
  background-color: #dc3545;
  color: black;
}

.dark #btnEliminar:hover {
  background-color: #b72433;
}

.dark .modalShowConfirm-Si {
  background-color: #dc3545;
  color: white;
}

.dark .modalShowConfirm-no {
  background-color: #6c757d;
  color: white;
}

/* Estilos para la paginación */
.loading-indicator,
.no-data {
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
  color: #666;
}

.tipo-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.tipo-general       { background: #e9ecef; color: #495057; }
.tipo-materia_prima { background: #fff3cd; color: #856404; }
.tipo-producto_terminado { background: #d1e7dd; color: #0a5240; }

.btn-barcode {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  color: #495057;
  cursor: pointer;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.btn-barcode:hover {
  background: #c09d62;
  color: white;
  border-color: #c09d62;
  transform: scale(1.05);
}

/* ── Botones plantilla / importar ── */
.btn-plantilla {
  background: #198754 !important;
  color: #fff !important;
  border: none !important;
  padding: 8px 16px !important;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex !important;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.15s, box-shadow 0.15s;
}
.btn-plantilla:hover,
.btn-plantilla:focus {
  background: #146c43 !important;
  box-shadow: 0 2px 8px rgba(25, 135, 84, 0.35);
  color: #fff !important;
}

.btn-importar {
  background: #0d6efd !important;
  color: #fff !important;
  border: none !important;
  padding: 8px 16px !important;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex !important;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.15s, box-shadow 0.15s;
}
.btn-importar:hover,
.btn-importar:focus {
  background: #0b5ed7 !important;
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.35);
  color: #fff !important;
}

/* ── Modal importar ── */
.modal-importar {
  width: 800px;
  max-width: 96vw;
  max-height: 90vh;
  overflow-y: auto;
}

.import-instrucciones {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: #495057;
}
.import-instrucciones ul { margin: 8px 0 0 18px; }
.import-instrucciones li { margin-bottom: 4px; }

.import-dropzone {
  border: 2px dashed #adb5bd;
  border-radius: 10px;
  padding: 32px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: #fafafa;
}
.import-dropzone.dragging, .import-dropzone:hover {
  border-color: #0d6efd;
  background: #e7f1ff;
}
.import-dropzone.has-file { border-color: #198754; background: #f0faf4; }

.import-drop-icon {
  font-size: 2.8rem;
  color: #adb5bd;
  display: block;
  margin-bottom: 10px;
}
.import-drop-icon.selected { color: #198754; }
.import-drop-text { font-size: 0.9rem; color: #495057; margin-bottom: 4px; }
.import-drop-hint { font-size: 0.78rem; color: #868e96; }

.import-error-msg {
  color: #dc3545;
  font-size: 0.84rem;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Preview / resultado */
.import-resumen-preview {
  display: flex;
  gap: 16px;
  margin-bottom: 14px;
}
.import-stat {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 0.88rem;
}
.import-stat.ok  { background: #d1e7dd; color: #0f5132; }
.import-stat.ok i { color: #198754; font-size: 1.3rem; }
.import-stat.skip { background: #fff3cd; color: #664d03; }
.import-stat.skip i { color: #ffc107; font-size: 1.3rem; }
.stat-num { font-size: 1.5rem; font-weight: 700; margin-right: 4px; }
.stat-lbl { font-size: 0.8rem; }

.import-table-wrap {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}
.import-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.import-table th {
  background: #343a40;
  color: #fff;
  padding: 8px 10px;
  text-align: left;
  position: sticky;
  top: 0;
}
.import-table td {
  padding: 7px 10px;
  border-bottom: 1px solid #dee2e6;
  color: #212529 !important;
}
.fila-ok  { background: #fff !important; }
.fila-ok:hover  { background: #f0faf4 !important; }
.fila-error { background: #fff5f5 !important; }
.fila-error:hover { background: #ffe4e4 !important; }

.badge-ok  { color: #198754; font-size: 0.8rem; display:inline-flex; align-items:center; gap:3px; }
.badge-err { color: #dc3545; font-size: 0.78rem; display:inline-flex; align-items:center; gap:3px;
             max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.import-resultado {
  text-align: center;
  padding: 24px 0;
}
.resultado-icon { font-size: 3rem; margin-bottom: 10px; }
.resultado-icon.ok { color: #198754; }
.resultado-warn { color: #856404; font-size: 0.88rem; margin-top: 4px; }

.btn-secondary {
  background: #6c757d;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}
.btn-secondary:hover { background: #5c636a; }
.btn-success {
  background: #198754;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}
.btn-success:hover { background: #146c43; }
.btn-success:disabled, .btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
</style>
